/* ────────────────────────────────────────
   기술적 지표 순수 함수 모음 (1차: MA·볼린저·RSI·MACD)
   - 입력 = close 가격 배열(오래된→최신 순), 출력 = 같은 길이 배열(앞쪽 null 패딩)
   - 차트 매핑 편의를 위해 입력과 인덱스 정렬을 맞춤(데이터 없는 구간 null)
   - 표준편차는 모집단(N) 기준 — 대부분의 차트 플랫폼(TradingView 등) 관행
   - TDD: indicators.test.ts 의 수기 계산값으로 검증 (feedback_tdd_auto)
──────────────────────────────────────── */

export type Num = number | null;

/** 단순이동평균 SMA — i < period-1 구간은 null */
export function sma(values: number[], period: number): Num[] {
  const out: Num[] = new Array(values.length).fill(null);
  if (period <= 0) return out;
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
    if (i >= period) sum -= values[i - period]; // 윈도우 밖 제거
    if (i >= period - 1) out[i] = sum / period;
  }
  return out;
}

/** 지수이동평균 EMA — 첫 값(index period-1)은 SMA로 시드, 이후 가중 */
export function ema(values: number[], period: number): Num[] {
  const out: Num[] = new Array(values.length).fill(null);
  if (period <= 0 || values.length < period) return out;
  const k = 2 / (period + 1);
  // 시드 = 첫 period개의 SMA
  let seed = 0;
  for (let i = 0; i < period; i++) seed += values[i];
  let prev = seed / period;
  out[period - 1] = prev;
  for (let i = period; i < values.length; i++) {
    prev = values[i] * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

/** 볼린저밴드 — 중심=SMA(period), 상/하단 = 중심 ± mult×표준편차(모집단) */
export function bollinger(
  values: number[],
  period = 20,
  mult = 2,
): { middle: Num[]; upper: Num[]; lower: Num[] } {
  const middle = sma(values, period);
  const upper: Num[] = new Array(values.length).fill(null);
  const lower: Num[] = new Array(values.length).fill(null);
  for (let i = period - 1; i < values.length; i++) {
    const m = middle[i];
    if (m == null) continue;
    let variance = 0;
    for (let j = i - period + 1; j <= i; j++) {
      const d = values[j] - m;
      variance += d * d;
    }
    const sd = Math.sqrt(variance / period); // 모집단 표준편차
    upper[i] = m + mult * sd;
    lower[i] = m - mult * sd;
  }
  return { middle, upper, lower };
}

/** RSI — Wilder 평활(period=14 기본). 첫 값은 index=period */
export function rsi(values: number[], period = 14): Num[] {
  const out: Num[] = new Array(values.length).fill(null);
  if (values.length <= period) return out;
  let gainSum = 0;
  let lossSum = 0;
  // 첫 평균(단순 평균) — change[1..period]
  for (let i = 1; i <= period; i++) {
    const ch = values[i] - values[i - 1];
    if (ch >= 0) gainSum += ch;
    else lossSum -= ch;
  }
  let avgGain = gainSum / period;
  let avgLoss = lossSum / period;
  out[period] = rsiFrom(avgGain, avgLoss);
  // 이후 Wilder 평활
  for (let i = period + 1; i < values.length; i++) {
    const ch = values[i] - values[i - 1];
    const gain = ch >= 0 ? ch : 0;
    const loss = ch < 0 ? -ch : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    out[i] = rsiFrom(avgGain, avgLoss);
  }
  return out;
}

function rsiFrom(avgGain: number, avgLoss: number): number {
  if (avgLoss === 0) return avgGain === 0 ? 50 : 100; // 손실 0 = 100(둘다 0이면 중립 50)
  const rs = avgGain / avgLoss;
  return 100 - 100 / (1 + rs);
}

/** MACD — macd=EMA(fast)-EMA(slow), signal=EMA(signal, macd), hist=macd-signal */
export function macd(
  values: number[],
  fast = 12,
  slow = 26,
  signal = 9,
): { macd: Num[]; signal: Num[]; histogram: Num[] } {
  const emaFast = ema(values, fast);
  const emaSlow = ema(values, slow);
  const macdLine: Num[] = new Array(values.length).fill(null);
  for (let i = 0; i < values.length; i++) {
    if (emaFast[i] != null && emaSlow[i] != null) {
      macdLine[i] = (emaFast[i] as number) - (emaSlow[i] as number);
    }
  }
  // signal = macd의 EMA — null 아닌 구간만 모아 EMA 후 원위치 매핑
  const compactIdx: number[] = [];
  const compactVal: number[] = [];
  for (let i = 0; i < macdLine.length; i++) {
    if (macdLine[i] != null) {
      compactIdx.push(i);
      compactVal.push(macdLine[i] as number);
    }
  }
  const sigCompact = ema(compactVal, signal);
  const signalLine: Num[] = new Array(values.length).fill(null);
  const histogram: Num[] = new Array(values.length).fill(null);
  for (let k = 0; k < compactIdx.length; k++) {
    const i = compactIdx[k];
    if (sigCompact[k] != null) {
      signalLine[i] = sigCompact[k];
      histogram[i] = (macdLine[i] as number) - (sigCompact[k] as number);
    }
  }
  return { macd: macdLine, signal: signalLine, histogram };
}
